import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { orderAPI, cardAPI } from '@/lib/api';
import { 
  Package, 
  Wallet, 
  CreditCard,
  ArrowRight,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function DashboardHome() {
  const { user, getQRCode } = useAuth();
  const [stats, setStats] = useState({ orders: 0, cards: 0, linkedCards: 0 });
  const [loading, setLoading] = useState(true);
  const qrCode = getQRCode();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [ordersRes, cardsRes] = await Promise.all([
          orderAPI.getAll(),
          cardAPI.getAll()
        ]);
        
        const cards = cardsRes.data || [];
        setStats({
          orders: ordersRes.data?.length || 0,
          cards: cards.length,
          linkedCards: cards.filter(c => c.isLinked).length
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    { icon: Package, label: 'Total Orders', value: stats.orders, color: 'from-violet-500 to-purple-600' },
    { icon: Wallet, label: 'My Cards', value: stats.cards, color: 'from-emerald-500 to-teal-600' },
    { icon: CreditCard, label: 'Linked Cards', value: stats.linkedCards, color: 'from-orange-500 to-red-600' },
  ];

  return (
    <div data-testid="dashboard-home">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
        </h1>
        <p className="text-slate-400 text-sm md:text-base">Here's an overview of your MeTag account</p>
      </div>

      {/* QR Code Alert */}
      {qrCode && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 glass rounded-2xl p-6 border border-emerald-500/30"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">Card Detected!</h3>
              <p className="text-slate-400 mb-3">
                You have a MeTag card ready to be linked. Go to My Cards to associate it with your profile URL.
              </p>
              <Link to="/dashboard/cards">
                <Button className="btn-primary flex items-center gap-2" data-testid="link-card-btn">
                  Link Your Card
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass rounded-2xl p-6"
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4`}>
              <stat.icon className="w-6 h-6 text-white" />
            </div>
            <p className="text-slate-400 text-sm mb-1">{stat.label}</p>
            <p className="text-3xl font-bold text-white">
              {loading ? '-' : stat.value}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Link to="/dashboard/orders" className="glass rounded-2xl p-6 hover:border-violet-500/30 transition-colors border border-transparent">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">View Orders</h3>
              <p className="text-slate-400">Track your order status and shipping</p>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400" />
          </div>
        </Link>

        <Link to="/dashboard/cards" className="glass rounded-2xl p-6 hover:border-violet-500/30 transition-colors border border-transparent">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">Manage Cards</h3>
              <p className="text-slate-400">Link or update your card URLs</p>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400" />
          </div>
        </Link>
      </div>
    </div>
  );
}
